import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Alert, Spinner, Badge, Form, Modal, Row, Col, Pagination } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faCheck, faTimes, faTrash, faFilter } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function RecensioniManagement() {
    const [recensioni, setRecensioni] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Filtri
    const [filtroStato, setFiltroStato] = useState('da_moderare'); // approvate, rifiutate, da_moderare
    const [filtroProdotto, setFiltroProdotto] = useState(''); // film, musica, collectible
    
    // Paginazione
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [total, setTotal] = useState(0);
    const [hasNext, setHasNext] = useState(false);
    
    // Modal moderazione
    const [showModal, setShowModal] = useState(false);
    const [recensioneSelezionata, setRecensioneSelezionata] = useState(null);
    const [motivoRifiuto, setMotivoRifiuto] = useState('');
    const [azioneModal, setAzioneModal] = useState(''); // 'approva' o 'rifiuta'

    useEffect(() => {
        fetchRecensioni();
    }, [page, filtroStato, filtroProdotto]);

    const fetchRecensioni = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('access_token');
            
            let url = `${API_URL}/api/recensioni/admin/tutte?page=${page}&page_size=${pageSize}`;
            if (filtroStato) url += `&filtro_stato=${filtroStato}`;
            if (filtroProdotto) url += `&tipo_prodotto=${filtroProdotto}`;
            
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setRecensioni(response.data.items);
            setTotal(response.data.total);
            setHasNext(response.data.has_next);
        } catch (err) {
            console.error('Error fetching recensioni:', err);
            setError('Errore nel caricamento delle recensioni');
        } finally {
            setLoading(false);
        }
    };

    const handleModera = async (approvare) => {
        try {
            const token = localStorage.getItem('access_token');
            
            await axios.post(
                `${API_URL}/api/recensioni/admin/${recensioneSelezionata.id}/modera`,
                {
                    approvata: approvare,
                    motivo_rifiuto: approvare ? null : motivoRifiuto
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setSuccess(`Recensione ${approvare ? 'approvata' : 'rifiutata'} con successo!`);
            setShowModal(false);
            setRecensioneSelezionata(null);
            setMotivoRifiuto('');
            fetchRecensioni();
            
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Error moderating recensione:', err);
            setError(err.response?.data?.detail || 'Errore nella moderazione');
        }
    };

    const handleElimina = async (recensioneId) => {
        if (!window.confirm('Sei sicuro di voler eliminare questa recensione?')) return;
        
        try {
            const token = localStorage.getItem('access_token');
            
            await axios.delete(
                `${API_URL}/api/recensioni/admin/${recensioneId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setSuccess('Recensione eliminata con successo!');
            fetchRecensioni();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            console.error('Error deleting recensione:', err);
            setError(err.response?.data?.detail || 'Errore nell\'eliminazione');
        }
    };

    const openModaleModera = (recensione, azione) => {
        setRecensioneSelezionata(recensione);
        setAzioneModal(azione);
        setShowModal(true);
    };

    const renderStelle = (valutazione) => {
        return (
            <>
                {[1, 2, 3, 4, 5].map(i => (
                    <FontAwesomeIcon
                        key={i}
                        icon={faStar}
                        className={i <= valutazione ? 'text-warning' : 'text-muted'}
                    />
                ))}
            </>
        );
    };

    const getStatoBadge = (recensione) => {
        if (recensione.approvata) {
            return <Badge bg="success">Approvata</Badge>;
        } else if (recensione.moderata_da) {
            return <Badge bg="danger">Rifiutata</Badge>;
        } else {
            return <Badge bg="warning" text="dark">Da Moderare</Badge>;
        }
    };

    const getTipoProdottoBadge = (tipo) => {
        const colors = {
            film: 'primary',
            musica: 'info',
            collectible: 'secondary'
        };
        return <Badge bg={colors[tipo] || 'secondary'}>{tipo}</Badge>;
    };

    const totalPages = Math.ceil(total / pageSize);

    if (loading && recensioni.length === 0) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Caricamento recensioni...</p>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <div className="d-flex align-items-center mb-4">
                <FontAwesomeIcon icon={faStar} size="2x" className="text-warning me-3" />
                <div>
                    <h1 className="mb-0">Gestione Recensioni</h1>
                    <p className="text-muted mb-0">Modera e gestisci le recensioni dei clienti</p>
                </div>
            </div>

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

            {/* Filtri */}
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <FontAwesomeIcon icon={faFilter} className="me-2" />
                    Filtri
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Stato</Form.Label>
                                <Form.Select
                                    value={filtroStato}
                                    onChange={(e) => {
                                        setFiltroStato(e.target.value);
                                        setPage(1);
                                    }}
                                >
                                    <option value="">Tutte</option>
                                    <option value="da_moderare">Da Moderare</option>
                                    <option value="approvate">Approvate</option>
                                    <option value="rifiutate">Rifiutate</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Tipo Prodotto</Form.Label>
                                <Form.Select
                                    value={filtroProdotto}
                                    onChange={(e) => {
                                        setFiltroProdotto(e.target.value);
                                        setPage(1);
                                    }}
                                >
                                    <option value="">Tutti</option>
                                    <option value="film">Film</option>
                                    <option value="musica">Musica</option>
                                    <option value="collectible">Collectible</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Statistiche */}
            <Card className="mb-4">
                <Card.Body>
                    <Row className="text-center">
                        <Col md={4}>
                            <h3 className="text-primary">{total}</h3>
                            <p className="text-muted mb-0">Recensioni Totali</p>
                        </Col>
                        <Col md={4}>
                            <h3 className="text-warning">
                                {recensioni.filter(r => !r.approvata && !r.moderata_da).length}
                            </h3>
                            <p className="text-muted mb-0">Da Moderare</p>
                        </Col>
                        <Col md={4}>
                            <h3 className="text-success">
                                {recensioni.filter(r => r.approvata).length}
                            </h3>
                            <p className="text-muted mb-0">Approvate</p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Tabella recensioni */}
            <Card>
                <Card.Body>
                    {recensioni.length === 0 ? (
                        <Alert variant="info">Nessuna recensione trovata con i filtri selezionati.</Alert>
                    ) : (
                        <div className="table-responsive">
                            <Table hover>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Cliente</th>
                                        <th>Prodotto</th>
                                        <th>Tipo</th>
                                        <th>Valutazione</th>
                                        <th>Titolo</th>
                                        <th>Data</th>
                                        <th>Stato</th>
                                        <th>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recensioni.map((rec) => (
                                        <tr key={rec.id}>
                                            <td>{rec.id}</td>
                                            <td>
                                                {rec.cliente_nome} {rec.cliente_cognome}
                                                <br />
                                                <small className="text-muted">ID: {rec.cliente_id}</small>
                                            </td>
                                            <td>
                                                {rec.prodotto_titolo || `Prodotto #${rec.prodotto_id}`}
                                            </td>
                                            <td>{getTipoProdottoBadge(rec.tipo_prodotto)}</td>
                                            <td>{renderStelle(rec.valutazione)}</td>
                                            <td>
                                                <strong>{rec.titolo || 'Nessun titolo'}</strong>
                                                <br />
                                                <small className="text-muted">
                                                    {rec.testo ? rec.testo.substring(0, 60) + '...' : 'Nessun testo'}
                                                </small>
                                            </td>
                                            <td>
                                                {new Date(rec.data_creazione).toLocaleDateString('it-IT')}
                                            </td>
                                            <td>{getStatoBadge(rec)}</td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    {!rec.approvata && !rec.moderata_da && (
                                                        <>
                                                            <Button
                                                                size="sm"
                                                                variant="success"
                                                                onClick={() => openModaleModera(rec, 'approva')}
                                                                title="Approva"
                                                            >
                                                                <FontAwesomeIcon icon={faCheck} />
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="warning"
                                                                onClick={() => openModaleModera(rec, 'rifiuta')}
                                                                title="Rifiuta"
                                                            >
                                                                <FontAwesomeIcon icon={faTimes} />
                                                            </Button>
                                                        </>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="danger"
                                                        onClick={() => handleElimina(rec.id)}
                                                        title="Elimina"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    {/* Paginazione */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-3">
                            <Pagination>
                                <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
                                <Pagination.Prev onClick={() => setPage(page - 1)} disabled={page === 1} />
                                
                                {[...Array(totalPages)].map((_, idx) => {
                                    const pageNum = idx + 1;
                                    if (
                                        pageNum === 1 ||
                                        pageNum === totalPages ||
                                        (pageNum >= page - 2 && pageNum <= page + 2)
                                    ) {
                                        return (
                                            <Pagination.Item
                                                key={pageNum}
                                                active={pageNum === page}
                                                onClick={() => setPage(pageNum)}
                                            >
                                                {pageNum}
                                            </Pagination.Item>
                                        );
                                    } else if (pageNum === page - 3 || pageNum === page + 3) {
                                        return <Pagination.Ellipsis key={pageNum} />;
                                    }
                                    return null;
                                })}
                                
                                <Pagination.Next onClick={() => setPage(page + 1)} disabled={!hasNext} />
                                <Pagination.Last onClick={() => setPage(totalPages)} disabled={page === totalPages} />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Modal Moderazione */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {azioneModal === 'approva' ? 'Approva Recensione' : 'Rifiuta Recensione'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {recensioneSelezionata && (
                        <>
                            <h5>{recensioneSelezionata.prodotto_titolo}</h5>
                            <p className="text-muted">
                                Di: {recensioneSelezionata.cliente_nome} {recensioneSelezionata.cliente_cognome}
                            </p>
                            
                            <div className="mb-3">
                                <strong>Valutazione:</strong> {renderStelle(recensioneSelezionata.valutazione)}
                            </div>
                            
                            {recensioneSelezionata.titolo && (
                                <div className="mb-3">
                                    <strong>Titolo:</strong> {recensioneSelezionata.titolo}
                                </div>
                            )}
                            
                            {recensioneSelezionata.testo && (
                                <div className="mb-3">
                                    <strong>Recensione:</strong>
                                    <p className="mt-2 p-3 bg-light rounded">{recensioneSelezionata.testo}</p>
                                </div>
                            )}
                            
                            {azioneModal === 'rifiuta' && (
                                <Form.Group>
                                    <Form.Label>Motivo del rifiuto *</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={motivoRifiuto}
                                        onChange={(e) => setMotivoRifiuto(e.target.value)}
                                        placeholder="Specifica il motivo del rifiuto..."
                                        required
                                    />
                                </Form.Group>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Annulla
                    </Button>
                    <Button
                        variant={azioneModal === 'approva' ? 'success' : 'warning'}
                        onClick={() => handleModera(azioneModal === 'approva')}
                        disabled={azioneModal === 'rifiuta' && !motivoRifiuto}
                    >
                        {azioneModal === 'approva' ? 'Approva' : 'Rifiuta'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
